/**
 * useSubscriptionPortalState — les GESTES du portail Abonnement : confirmations, écritures,
 * accusés, et les parcours restés legacy qu'on ne fait qu'ouvrir.
 *
 * Huit des douze appels du legacy vivent ici, à l'identique :
 *
 *   3. `logParentModeAction`         — signaux d'intent, throttlés par `createIntentSignalGate`
 *                                      (`:196-208`) : `BANNER_CLICKED` + `MODAL_OPENED` à
 *                                      l'ouverture du composeur (`:1334-1335`), `EXTEND_CLICKED`
 *                                      au clic « Prolonger » (`:1359`)
 *   4. `cancelParentPause`           — corps `{}`, après confirmation (`:255`)
 *   5. `getParentPauseBalance` + `getSubscribePricing` — au succès de 4, avec l'accusé (`:264-273`)
 *   6. `getSubscribePricing`         — au succès de 9, avec l'accusé (`:277-291`)
 *   8. `createBillingPortalSession`  — corps `{flow:"payment_method_update"}` ou `{}` (`:605-638`).
 *                                      **L'onglet web est ouvert AVANT l'appel** et reçoit l'URL
 *                                      ensuite : un `window.open` après l'aller-retour réseau se
 *                                      fait avaler par le bloqueur de pop-ups.
 *   9. `extendSummerSubscription`    — sans corps, après confirmation (`:1365`)
 *  12. `navigation.push("cancel-reason", { studentId })` — après confirmation (`:641-660`)
 *
 * Les quatre parcours qui restent legacy (pause, heures supplémentaires, composeur d'activation,
 * résiliation) et Intercom ne sont pas appelés d'ici : leurs ouvreurs arrivent en props, posés
 * par l'enveloppe du pont.
 *
 * Les deux écritures à confirmation (4 et 9) passent par `useApiMutation`. Leur confirmation se
 * ferme dès l'appui : le bandeau est la seule surface qui reste pour dire un refus du serveur, là
 * où le legacy ouvrait son dialogue « Erreur » (`useApiStatus(…, null, true, …)`). Le portail
 * Stripe (8) reste écrit à la main : il a une garde de plus, l'onglet pré-ouvert à refermer.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useFocusEffect, useNavigation, useRoute } from "@react-navigation/native"
import * as amplitude from "@amplitude/analytics-react-native"

import { apiIdle, apiRequest } from "../../../../../../data/redux/actions/api"
import { subscriptionService } from "../../../../../../data/services/subscriptionService"
import { formatActivationDate } from "../../../../adapters/parent/shared/activationDisplay"
import { IS_WEB, openExternalUrl } from "../../../hooks/openExternalUrl"
import { useApiMutation } from "../../../hooks/useApiMutation"
import { useGesture } from "../../../hooks/gestureTracking"

/** `ACTIVATION_TRACKING_SOURCE` de `ActivationComposerScreen.js:51`. */
const ACTIVATION_TRACKING_SOURCE = "parent_subscription.self_serve"

/** `INTENT_SIGNALS` de `@common/utils/intentSignals` — les GESTES, jamais les vues. */
const INTENT_SIGNALS = {
    BANNER_CLICKED: "activation_banner_clicked",
    MODAL_OPENED: "activation_modal_opened",
    EXTEND_CLICKED: "activation_extend_clicked",
}

/** Replis quand le serveur refuse sans dire pourquoi — son message, quand il en a un, prime. */
const CANCEL_PAUSE_FAILED = "Nous n'avons pas pu annuler la pause. Réessayez dans un instant."
const EXTEND_FAILED = "Nous n'avons pas pu enregistrer la prolongation. Réessayez dans un instant."

/** Anti-bruit : un même signal part au plus UNE fois par session d'écran. */
function createIntentSignalGate() {
    const sent = new Set<string>()
    return (action: string) => {
        if (!action || sent.has(action)) return false
        sent.add(action)
        return true
    }
}

export type PortalConfirm = {
    title: string
    message: string
    confirmLabel: string
    cancelLabel: string
    danger: boolean
}

export type PortalSnackbar = { message: string; tone: "success" | "danger" }

type UiState = {
    confirm: (PortalConfirm & { action: ConfirmAction }) | null
    snackbar: PortalSnackbar | null
}

type ConfirmAction = "cancel-pause" | "extend" | "cancel-subscription"

type UiAction =
    | { type: "OPEN_CONFIRM"; confirm: PortalConfirm & { action: ConfirmAction } }
    | { type: "CLOSE_CONFIRM" }
    | { type: "SHOW_SNACKBAR"; snackbar: PortalSnackbar }
    | { type: "HIDE_SNACKBAR" }

const INITIAL: UiState = { confirm: null, snackbar: null }

function reducer(state: UiState, action: UiAction): UiState {
    switch (action.type) {
        case "OPEN_CONFIRM":
            return { ...state, confirm: action.confirm }
        case "CLOSE_CONFIRM":
            return { ...state, confirm: null }
        case "SHOW_SNACKBAR":
            // Une confirmation encore ouverte derrière un accusé : le geste est joué, on ferme.
            return { confirm: null, snackbar: action.snackbar }
        case "HIDE_SNACKBAR":
            return { ...state, snackbar: null }
        default:
            return state
    }
}

type ApiSlice = {
    request?: boolean
    success?: boolean
    error?: unknown
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data?: any
}

export type SubscriptionPortalStateArgs = {
    studentId: string | number | null
    activationType: string
    /** Textes du view-model. Les deux premiers ne se composent qu'au clic. */
    cancelPauseConfirm: () => { title: string; message: string }
    /** Absent ⇒ rien à prolonger. */
    extendConfirm: (() => { title: string; message: string }) | null
    cancelSubscriptionConfirm: { title: string; message: string }
    pauseCancelledMessage: string
    /** Rechargements à jouer après une écriture (`use*Data`). */
    refreshPricing: () => void
    refreshPauseBalance: () => void
    /** La carte « suite à activer » est rendue : l'événement de vue part une fois. */
    activationBannerVisible: boolean
    /**
     * Ouvre la pause — la feuille du design system, ou la modale legacy quand son drapeau est
     * éteint. L'écran « Alternatives » de la résiliation propose la pause et revient ICI en le
     * demandant par un paramètre de route (`CancelAlternativesScreen.js:180`), avec sa provenance :
     * la pause proposée contre un départ va jusqu'à 1 mois.
     */
    onTriggerPause: (options: { fromCancellation: boolean }) => void
    /** Ouvre « Modifier mes créneaux » — sur sa demande en cours, quand l'accueil le demande (`triggerSlotChange`). */
    onTriggerSlotChange?: () => void
}

export function useSubscriptionPortalState(args: SubscriptionPortalStateArgs) {
    const {
        studentId,
        activationType,
        cancelPauseConfirm,
        extendConfirm,
        cancelSubscriptionConfirm,
        pauseCancelledMessage,
        refreshPricing,
        refreshPauseBalance,
        activationBannerVisible,
        onTriggerPause,
        onTriggerSlotChange,
    } = args

    const dispatch = useDispatch()
    const navigation = useNavigation<{
        push: (name: string, params?: Record<string, unknown>) => void
        navigate: (name: string) => void
        setParams: (params: Record<string, unknown>) => void
    }>()
    const route = useRoute<{
        key: string
        name: string
        params?: { triggerPause?: boolean; fromCancellation?: boolean; triggerSlotChange?: boolean }
    }>()
    const [ui, dispatchUi] = useReducer(reducer, INITIAL)

    const portalReq = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state: any) => state?.api?.createBillingPortalSession as ApiSlice | undefined
    )

    /**
     * L'intention posée par l'écran « Alternatives » du parcours de résiliation. On vide le
     * paramètre juste après l'avoir consommé, sinon un re-focus rouvrirait la feuille.
     */
    useFocusEffect(
        useCallback(() => {
            if (!route.params?.triggerPause) return
            onTriggerPause({ fromCancellation: !!route.params.fromCancellation })
            navigation.setParams({ triggerPause: undefined, fromCancellation: undefined })
        }, [route.params?.triggerPause, route.params?.fromCancellation, navigation, onTriggerPause])
    )

    /** Une ligne de « Demandes en cours » sur l'accueil ouvre le détail ici, là où vit le parcours. */
    useFocusEffect(
        useCallback(() => {
            if (!route.params?.triggerSlotChange) return
            onTriggerSlotChange?.()
            navigation.setParams({ triggerSlotChange: undefined })
        }, [route.params?.triggerSlotChange, navigation, onTriggerSlotChange])
    )

    // ── Signaux d'intent (appel 3) ────────────────────────────────────────────────────────
    const intentSignalGate = useRef(createIntentSignalGate())
    const sendIntentSignal = useCallback(
        (action: string) => {
            if (intentSignalGate.current(action)) {
                dispatch(apiRequest(subscriptionService.logParentModeAction, {}, { action }) as never)
            }
        },
        [dispatch]
    )

    // ── « Et ensuite ? » : la vue, puis le composeur ──────────────────────────────────────
    const activationBannerSeen = useRef(false)
    useEffect(() => {
        if (!activationBannerVisible || activationBannerSeen.current) return
        activationBannerSeen.current = true
        amplitude.track(`${ACTIVATION_TRACKING_SOURCE}.banner_viewed`, {
            source: "self_serve",
            activation_type: activationType,
        })
    }, [activationBannerVisible, activationType])

    const openActivationComposer = useCallback(() => {
        amplitude.track(`${ACTIVATION_TRACKING_SOURCE}.banner_clicked`, {
            source: "self_serve",
            activation_type: activationType,
        })
        // Deux signaux : le tap sur le slot du portail ET l'ouverture effective.
        sendIntentSignal(INTENT_SIGNALS.BANNER_CLICKED)
        sendIntentSignal(INTENT_SIGNALS.MODAL_OPENED)
        navigation.navigate("activation/composer")
    }, [activationType, navigation, sendIntentSignal])

    /** Le refus d'une écriture dont la confirmation est déjà fermée. */
    const showFailure = useCallback(
        (message: string) => dispatchUi({ type: "SHOW_SNACKBAR", snackbar: { message, tone: "danger" } }),
        []
    )

    // ── Annulation de la pause manuelle (appels 4 et 5) ───────────────────────────────────
    const { start: startCancelPause } = useApiMutation({
        service: subscriptionService.cancelParentPause,
        gesture: "subscription.pause_cancel",
        fallback: CANCEL_PAUSE_FAILED,
        onSuccess: () => {
            dispatchUi({ type: "SHOW_SNACKBAR", snackbar: { message: pauseCancelledMessage, tone: "success" } })
            refreshPauseBalance()
            refreshPricing()
        },
        onError: showFailure,
    })

    const askCancelPause = useCallback(() => {
        dispatchUi({
            type: "OPEN_CONFIRM",
            confirm: {
                action: "cancel-pause",
                ...cancelPauseConfirm(),
                confirmLabel: "Confirmer l'annulation",
                cancelLabel: "Retour",
                danger: false,
            },
        })
    }, [cancelPauseConfirm])

    // ── Prolongation du produit d'appel (appels 9 et 6) ───────────────────────────────────
    const { start: startExtend } = useApiMutation({
        service: subscriptionService.extendSummerSubscription,
        gesture: "subscription.extend",
        fallback: EXTEND_FAILED,
        onSuccess: (data) => {
            const newEnd = (data as { upcoming_activation?: { due_date?: string } } | undefined)?.upcoming_activation
                ?.due_date
            dispatchUi({
                type: "SHOW_SNACKBAR",
                snackbar: {
                    message: newEnd
                        ? `Prolongation confirmée jusqu'au ${formatActivationDate(newEnd)}.`
                        : "La prolongation a bien été enregistrée.",
                    tone: "success",
                },
            })
            refreshPricing()
        },
        onError: showFailure,
    })

    const askExtend = useCallback(() => {
        if (extendConfirm == null) return
        amplitude.track(`${ACTIVATION_TRACKING_SOURCE}.extend_product_clicked`, {
            activation_type: activationType,
        })
        // Intent FORT : le parent envisage de payer un mois de plus.
        sendIntentSignal(INTENT_SIGNALS.EXTEND_CLICKED)
        dispatchUi({
            type: "OPEN_CONFIRM",
            confirm: {
                action: "extend",
                ...extendConfirm(),
                confirmLabel: "Confirmer la prolongation",
                cancelLabel: "Retour",
                danger: false,
            },
        })
    }, [activationType, extendConfirm, sendIntentSignal])

    // ── Résiliation (appel 12) ────────────────────────────────────────────────────────────
    const askCancelSubscription = useCallback(() => {
        dispatchUi({
            type: "OPEN_CONFIRM",
            confirm: {
                action: "cancel-subscription",
                ...cancelSubscriptionConfirm,
                confirmLabel: "Continuer",
                cancelLabel: "Annuler",
                danger: true,
            },
        })
    }, [cancelSubscriptionConfirm])

    const confirmCurrent = useCallback(() => {
        const action = ui.confirm?.action
        if (action == null) return
        if (action === "cancel-pause") {
            startCancelPause()
        } else if (action === "extend") {
            startExtend()
        } else {
            navigation.push("cancel-reason", { studentId })
        }
        dispatchUi({ type: "CLOSE_CONFIRM" })
    }, [navigation, startCancelPause, startExtend, studentId, ui.confirm?.action])

    // ── Portail Stripe (appel 8) ──────────────────────────────────────────────────────────
    // La session est créée AU CLIC : générée au chargement, son URL éphémère meurt avant.
    const pendingPortalTabRef = useRef<Window | null>(null)
    const awaitingPortalRef = useRef(false)
    const handledPortalErrorRef = useRef(false)
    const portalGesture = useGesture("payment.billing_portal")

    const openBillingPortal = useCallback(
        (flow: string | null = null) => {
            if (IS_WEB && typeof window !== "undefined") {
                pendingPortalTabRef.current = window.open("", "_blank")
            }
            awaitingPortalRef.current = true
            handledPortalErrorRef.current = false
            dispatch(apiRequest(subscriptionService.createBillingPortalSession, {}, flow ? { flow } : {}) as never)
            portalGesture.submitted()
        },
        [dispatch, portalGesture]
    )

    useEffect(() => {
        if (!awaitingPortalRef.current || !portalReq?.success) return
        awaitingPortalRef.current = false
        portalGesture.succeeded()
        const url = portalReq.data?.url
        dispatch(apiIdle(subscriptionService.createBillingPortalSession) as never)
        if (!url) return
        // L'onglet a été ouvert AU CLIC : il attend son URL. Sans lui, l'ouverture normale.
        if (IS_WEB && pendingPortalTabRef.current) {
            pendingPortalTabRef.current.location = url
            pendingPortalTabRef.current = null
            return
        }
        openExternalUrl(url)
    }, [dispatch, portalReq?.success, portalReq?.data, portalGesture])

    useEffect(() => {
        if (!awaitingPortalRef.current || !portalReq?.error || handledPortalErrorRef.current) return
        handledPortalErrorRef.current = true
        awaitingPortalRef.current = false
        portalGesture.failed(portalReq.error)
        pendingPortalTabRef.current?.close?.()
        pendingPortalTabRef.current = null
        dispatchUi({
            type: "SHOW_SNACKBAR",
            snackbar: {
                // Le legacy tutoie ici, et seulement ici, dans tout le périmètre abonnement.
                message: "Impossible d'ouvrir l'espace de facturation. Réessayez dans un instant.",
                tone: "danger",
            },
        })
        dispatch(apiIdle(subscriptionService.createBillingPortalSession) as never)
    }, [dispatch, portalReq?.error, portalGesture])

    // Une tranche laissée en erreur ou en succès se rejouerait au prochain montage.
    useEffect(
        () => () => {
            dispatch(apiIdle(subscriptionService.createBillingPortalSession) as never)
        },
        [dispatch]
    )

    const actions = useMemo(
        () => ({
            openActivationComposer,
            askCancelPause,
            askExtend,
            askCancelSubscription,
            openPaymentMethod: () => openBillingPortal("payment_method_update"),
            openInvoices: () => openBillingPortal(),
        }),
        [askCancelPause, askCancelSubscription, askExtend, openActivationComposer, openBillingPortal]
    )

    return {
        confirm: ui.confirm,
        onConfirm: confirmCurrent,
        onCancelConfirm: useCallback(() => dispatchUi({ type: "CLOSE_CONFIRM" }), []),
        snackbar: ui.snackbar,
        onDismissSnackbar: useCallback(() => dispatchUi({ type: "HIDE_SNACKBAR" }), []),
        /** Accusé poussé par un autre parcours de l'écran (la date de prélèvement). */
        showSnackbar: useCallback(
            (message: string, tone: "success" | "danger" = "success") =>
                dispatchUi({ type: "SHOW_SNACKBAR", snackbar: { message, tone } }),
            []
        ),
        actions,
    }
}
